import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import {
  getAllRegistrationData,
  resetSuccess
} from '../../redux/slices/sellerRegSlice';
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  StepConnector
} from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import SellerRegistrationForm, {
  SellerRegistrationFormData
} from './SellerRegistrationForm';
import AccountSubmittedModal from './AccountSubmittedModal';
import {
  registrationRootSx,
  getCustomStepIconSx,
  getCustomStepIconNumberSx,
  leftDecorativePanelSx,
  airplaneImageSx,
  mainContentPanelSx,
  pageTitleWrapperSx,
  pageTitleSx,
  srOnlySx,
  stepConnectorSx,
  stepperSx,
  stepSx,
  stepLabelSx,
  stepContentSx,
  stepContentInnerBoxSx,
  step1DescriptionSx,
  pageBodySX
} from './SellerRegistration.styles';
import ConnectionApproach from './ConnectionApproach';
import { fontSize } from '@mui/system';
import i18n from '../../assets/i18n/i18n';

const airplanePng = `${process.env.PUBLIC_URL}/images/registration/airplane.png`;

const selectSellerReg = (state: any) => state.sellerReg;
const selectSubmitStatus = createSelector(
  [selectSellerReg],
  ({ submitSuccess }) => ({ submitSuccess })
);

// ─── OtherScreen ─────────────────────────────────────────────────────────────
// Shown when the user selects "Other" as their sole POS — replaces the entire
// registration flow so the user is fully outside the registration process.

const OtherScreen = () => {
  const { t } = useTranslation();
  return <ConnectionApproach />;
};

const steps = [{ id: 1 }, { id: 2 }, { id: 3 }, { id: 4 }];

const initialFormData: SellerRegistrationFormData = {
  itTeam: '',
  apiSchema: [],
  appliedPOS: [],
  sameAsBusinessContact: false,
  companyName: '',
  companyAddress: '',
  companyWebsite: '',
  bizFirstName: '',
  bizLastName: '',
  bizEmail: '',
  bizPhone: '',
  adminFirstName: '',
  adminLastName: '',
  adminEmail: '',
  adminPhone: '',
  customers: '',
  bizModel: [],
  ndcIntegration: '',
  ndcSpecify: '',
  flow: '',
  implDate: null
};

interface CustomStepIconProps {
  active?: boolean;
  completed?: boolean;
  icon?: React.ReactNode;
}

const CustomStepIcon = ({ active, completed, icon }: CustomStepIconProps) => (
  <Box aria-hidden="true" sx={getCustomStepIconSx(!!active, !!completed)}>
    {completed ? (
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#fff"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polyline points="20 6 9 17 4 12" />
      </svg>
    ) : (
      <Typography sx={getCustomStepIconNumberSx(!!active)}>{icon}</Typography>
    )}
  </Box>
);

const SellerRegistrationTest = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState(1);
  const [formData, setFormData] =
    useState<SellerRegistrationFormData>(initialFormData);
  const [showOtherScreen, setShowOtherScreen] = useState(false);
  const { submitSuccess } = useSelector(selectSubmitStatus);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const test = t('registrationStepsData.steps.1.asterisk');
  const [asteriskText1,asteriskText2] = test.split("*");

  // Fetch registration data on mount
  useEffect(() => {
    dispatch(getAllRegistrationData());
  }, [dispatch]);

  // Show AccountSubmittedModal when the API call succeeds
  useEffect(() => {
    if (submitSuccess) {
      setShowConfirmation(true);
      dispatch(resetSuccess());
    }
  }, [submitSuccess, dispatch]);

  const navigatedRef = useRef(false);

  const handleContinue = useCallback(() => {
    navigatedRef.current = true;
    setActiveStep((s) => Math.min(s + 1, steps.length));
  }, []);
  const handleBack = useCallback(() => {
    navigatedRef.current = true;
    setActiveStep((s) => Math.max(s - 1, 1));
  }, []);
  const handleSubmit = () => {
    /* TODO: wire up submission logic */
  };

  // Scroll + focus the form root whenever the user navigates between steps.
  // Uses setTimeout to wait for MUI StepContent's collapse/expand transition
  // (~300 ms) to finish before the new step's content is in the DOM.
  useEffect(() => {
    if (!navigatedRef.current) return;
    navigatedRef.current = false;
    const target = document.getElementById(
      'registration-heading'
    ) as HTMLElement | null;
    const id = requestAnimationFrame(() => {
      target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      target?.focus();
    });
    return () => cancelAnimationFrame(id);
  }, [activeStep]);

  // Generic update handler
  const updateFormData = useCallback(
    (updates: Partial<SellerRegistrationFormData>) => {
      setFormData((prev) => ({ ...prev, ...updates }));
    },
    []
  );

  if (showConfirmation) {
    return (
      <AccountSubmittedModal
        open={showConfirmation}
        onClose={() => setShowConfirmation(false)}
      />
    );
  }

  return (
    <Box sx={registrationRootSx}>
      {showOtherScreen ? (
        <OtherScreen />
      ) : (
        <>
          {/* Left panel — decorative, hidden from assistive technology */}
          <Box aria-hidden="true" sx={leftDecorativePanelSx}>
            <Box
              component="img"
              src={airplanePng}
              alt=""
              sx={airplaneImageSx}
            />
          </Box>

          {/* Right panel */}
          <Box
            component="main"
            aria-labelledby="registration-heading"
            sx={mainContentPanelSx}
          >
            {/* Page title */}
            <Box sx={pageTitleWrapperSx}>
              <Typography
                id="registration-heading"
                component="h1"
                sx={pageTitleSx}
              >
                {t('registrationStepsData.page.title')}
              </Typography>
              <Typography
              id="registration-body"
              sx={pageBodySX}
              >
                {t('registrationStepsData.page.body')}
              </Typography>
            </Box>

            {/* Visually-hidden live region — announces current step to screen readers on step change */}
            <Box
              role="status"
              aria-live="polite"
              aria-atomic="true"
              sx={srOnlySx}
            >
              {t('registrationStepsData.page.stepAnnouncement', {
                activeStep,
                totalSteps: steps.length,
                stepLabel: t(`registrationStepsData.steps.${activeStep}.label`)
              })}
            </Box>

            {/* Step list */}
            <Stepper
              activeStep={activeStep - 1}
              orientation="vertical"
              connector={<StepConnector sx={stepConnectorSx} />}
              role="list"
              aria-label={t('registrationStepsData.page.stepsAriaLabel')}
              sx={stepperSx}
            >
              {steps.map((step, index) => (
                <Step
                  key={step.id}
                  role="listitem"
                  aria-current={index === activeStep - 1 ? 'step' : undefined}
                  sx={stepSx}
                >
                  <StepLabel
                    StepIconComponent={CustomStepIcon}
                    sx={stepLabelSx}
                  >
                    <Box component='h2' sx={{all:'inherit'}}>{t(`registrationStepsData.steps.${step.id}.label`)}</Box>
                    
                    {index + 1 < activeStep && (
                      <Box component="span" sx={srOnlySx}>
                        {`, ${t('registrationStepsData.stepStatus.completed')}`}
                      </Box>
                    )}
                  </StepLabel>
                  <StepContent
                    TransitionProps={{ unmountOnExit: true }}
                    sx={stepContentSx}
                  >
                    <Box sx={stepContentInnerBoxSx}>
                      {/* Description — step 1 only */}
                      {step.id === 1 && (
                        <>
                        <Typography sx={step1DescriptionSx}>
                          {t('registrationStepsData.steps.1.description')}
                        </Typography>
                        <Typography sx={{fontSize:'16px', fontWeight:400, color:'#6D6D6F', fontStyle:'italic', pl: { sm: 0, xs: '48px' }}}>
                          {asteriskText1}(<span style={{color:'red'}}>*</span>){asteriskText2}
                        </Typography>
                        </>
                      )}
                      <SellerRegistrationForm
                        stepNumber={step.id}
                        onContinue={
                          step.id === steps.length
                            ? handleSubmit
                            : handleContinue
                        }
                        onBack={step.id > 1 ? handleBack : undefined}
                        formData={formData}
                        updateFormData={updateFormData}
                        onOtherSelected={() => setShowOtherScreen(true)}
                      />
                    </Box>
                  </StepContent>
                </Step>
              ))}
            </Stepper>
          </Box>
        </>
      )}
    </Box>
  );
};

export default SellerRegistrationTest;
